if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const isDrawer = this.cart && this.cart.tagName === 'CART-DRAWER';
        const quickAddModal = this.closest('quick-add-modal');
        // Pre-click empty state — openLoading drops is-empty optimistically (for
        // the loading width); restore it if the add fails.
        const wasEmpty = isDrawer ? this.cart.classList.contains('is-empty') : false;

        // OPTIMISTIC UI: open the drawer in a loading state immediately, in the
        // same tick as the click — before any FormData/network work — so it is
        // visible in ~1 frame. The ~0.9s server cart-write then reconciles in
        // via renderContents(). Skipped for quick-add (its modal is open) and
        // for the cart-notification UI.
        let didOptimisticOpen = false;
        if (isDrawer && !quickAddModal) {
          this.cart.openLoading(this.submitButton);
          didOptimisticOpen = true;
        }

        const formData = new FormData(this.form);
        // Scoped lookup for Appstle selling plan to prevent breaking bundles/complementary products
        const productCtx = this.closest('.card-wrapper, .product-card-wrapper, .quick-add-modal, .cart-drawer, .product__info-wrapper, .product-bundle-container');
        let appstleSellingPlanEl = null;

        if (productCtx) {
          appstleSellingPlanEl = productCtx.querySelector('[name="selling_plan"]:checked');
        } else {
          appstleSellingPlanEl = document.querySelector(`[data-section-id="${this.dataset.sectionId}"] [name="selling_plan"]:checked`);
        }

        let spInputs = [];
        let backupNames = [];
        // Temporarily blank global selling_plan inputs for cross-sell/bundle adds
        // so they aren't read as this line's plan. (Legacy Appstle-fetch guard;
        // harmless on the XHR path below, kept to preserve bundle behavior.)
        if (this.submitButton.classList.contains('bundle-btn') || this.closest('.cart_cta')) {
          spInputs = Array.from(document.querySelectorAll('[name="selling_plan"]'));
          spInputs.forEach(el => {
            backupNames.push(el.name);
            el.name = "";
          });
        }

        if (appstleSellingPlanEl?.value) {
          formData.append('selling_plan', appstleSellingPlanEl.value);
        } else if (productCtx) {
          // Fallback for AJAX-swapped product sections (e.g. after the product
          // family selector swaps in cached HTML) — Appstle injects its
          // [name="selling_plan"] radios client-side and doesn't re-inject after
          // section swaps, so the radios may not exist on the new product.
          // Read the variant's selling plan from the server-rendered JSON map
          // and trust the subscription toggle checkbox as the user's intent.
          //
          // Resolve via global.js's resolveSubToggle so we read the toggle of
          // the picker that is actually DISPLAYED. The stock picker and the V2
          // picker each render a toggle into the same section and exactly one
          // is visible per A/B arm; a plain id/selector lookup returned the
          // first in document order, which is the hidden one in the control
          // arm — so a customer who ticked the visible box was still charged
          // the one-time price. Local fallback keeps this working if global.js
          // hasn't run yet.
          const resolveToggle =
            window.resolveSubToggle ||
            function () {
              const all = document.querySelectorAll('[data-nx-sub-toggle]');
              for (const t of all) {
                const picker = t.closest('.vp-v2, variant-selects') || t;
                if (picker.offsetParent !== null || picker.getClientRects().length > 0) return t;
              }
              return all[0] || document.querySelector('#nx-sub-toggle-input');
            };
          const subToggle = resolveToggle();
          const planMapEl =
            productCtx.querySelector('[data-variant-selling-plans]') ||
            document.querySelector('[data-variant-selling-plans]');
          const variantId = formData.get('id');
          if (subToggle?.checked && planMapEl && variantId) {
            try {
              const planId = JSON.parse(planMapEl.textContent)[variantId];
              if (planId) {
                formData.append('selling_plan', planId);
              }
            } catch (e) {
              // Malformed JSON — fall through, item will be added as one-time purchase.
            }
          }
        }

        let sectionsList = '';
        const sectionsUrl = window.location.pathname;
        if (this.cart) {
          sectionsList = this.cart.getSectionsToRender().map((section) => section.id).join(',');
          this.cart.setActiveElement(document.activeElement);
        }

        // Add via XHR + items[] format. XHR is NOT intercepted by Appstle's
        // window.fetch monkey-patch, which otherwise adds ~1s of client-side
        // latency to EVERY add (measured 2026-06-02: fetch ~1.9s vs XHR ~0.9s).
        // The selling_plan set above attaches the subscription natively and
        // prices identically to the Appstle fetch path (verified). Sending
        // `sections` renders the updated drawer HTML inline with the add
        // (one round trip); addItemViaXhrWithSections falls back to a separate
        // sections fetch only if Shopify omits them.
        const item = {};
        const properties = {};
        for (const [k, v] of formData.entries()) {
          if (k === 'id') item.id = Number(v) || v;
          else if (k === 'quantity') item.quantity = Number(v) || 1;
          else if (k === 'selling_plan') item.selling_plan = Number(v) || v;
          else {
            // Only properties[xxx] fields are line item properties. Other form
            // fields (form_type, utf8, options[], variant radio names like
            // "Jar quantity-1") are form-internal noise.
            const m = k.match(/^properties\[(.+)\]$/);
            if (m) properties[m[1]] = v;
          }
        }
        if (typeof item.quantity === 'undefined') item.quantity = 1;
        if (Object.keys(properties).length > 0) item.properties = properties;

        this.addItemViaXhrWithSections(item, sectionsList, sectionsUrl)
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              // Nothing was added — undo the optimistic open so the customer
              // isn't left looking at an unchanged cart, and restore is-empty
              // (openLoading dropped it optimistically).
              if (didOptimisticOpen) {
                this.cart.clearLoading();
                if (wasEmpty) this.cart.classList.add('is-empty');
                this.cart.close();
              }

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              // The drawer is already open (optimistic). Reconcile the real cart
              // HTML in directly — no setTimeout hop and no second open animation
              // (renderContents detects the already-open drawer and just swaps
              // .drawer__inner, then re-traps focus).
              try {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                });
              } catch (e) {
                console.error(e);
              }
            }
          })
          .catch((e) => {
            console.error(e);
            if (didOptimisticOpen) {
              this.cart.clearLoading();
              if (wasEmpty) this.cart.classList.add('is-empty');
              this.cart.close();
            }
          })
          .finally(() => {
            if (spInputs && spInputs.length > 0) {
              spInputs.forEach((el, i) => {
                el.name = backupNames[i];
              });
            }

            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // Fallback add-to-cart used when Appstle's [name="selling_plan"] radios
      // weren't injected (e.g. after the product family selector AJAX-swapped
      // the main section). The standard fetch path can't be used because
      // Appstle monkey-patches window.fetch and strips selling_plan from
      // request bodies — including items[] format. XHR is not patched, so
      // selling_plan reaches Shopify intact.
      //
      // We send `sections` and `sections_url` in the items[] XHR body so
      // Shopify renders the cart drawer HTML inline with the add response,
      // saving a second round-trip. If the response omits the sections key
      // (older/inconsistent behavior with items[] format), fall back to a
      // separate sections fetch — same shape as before, just slower.
      //
      // Returns a response object shaped like the regular flat-format add
      // response so the existing then chain (renderContents, pubsub events)
      // works without modification.
      addItemViaXhrWithSections(item, sectionsList, sectionsUrl) {
        const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
        const xhrPromise = new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', cartAddUrl);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('Accept', 'application/json');
          xhr.onload = () => {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) { reject(e); }
          };
          xhr.onerror = () => reject(new Error('Network error'));
          const body = { items: [item] };
          if (sectionsList) {
            body.sections = sectionsList;
            body.sections_url = sectionsUrl;
          }
          xhr.send(JSON.stringify(body));
        });

        return xhrPromise.then((addResp) => {
          // If the add itself errored, return the error response so the
          // existing `if (response.status)` branch handles it.
          if (addResp && addResp.status && Number(addResp.status) >= 400) {
            return addResp;
          }
          if (!sectionsList) {
            const first = (addResp.items && addResp.items[0]) || {};
            return Object.assign({}, first, { items: addResp.items, sections: {} });
          }
          // Fast path: sections came back inline with the add response —
          // skip the second round-trip entirely.
          if (addResp.sections && Object.keys(addResp.sections).length > 0) {
            const first = (addResp.items && addResp.items[0]) || {};
            return Object.assign({}, first, {
              items: addResp.items,
              sections: addResp.sections,
              id: first.variant_id || first.id,
            });
          }
          // Fallback: Shopify didn't include sections in the items[]
          // response. Fetch them in a second round-trip.
          const sep = sectionsUrl.indexOf('?') >= 0 ? '&' : '?';
          const sectionsFetchUrl = sectionsUrl + sep + 'sections=' + encodeURIComponent(sectionsList);
          return fetch(sectionsFetchUrl)
            .then((r) => r.json())
            .catch(() => ({}))
            .then((sections) => {
              const first = (addResp.items && addResp.items[0]) || {};
              return Object.assign({}, first, {
                items: addResp.items,
                sections: sections,
                id: first.variant_id || first.id,
              });
            });
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
